/**
 * "I want my lawn to look better" — the free lawn walkthrough, booked by the customer.
 *
 * Owner, Oct 20 2026: "I want my lawn to look better button where a professional comes out and gives them a
 * runthrough. eg me, base it on my calendar." One outlined button wherever a customer already is (their page, a
 * bid, a bill), never above Accept or Pay — one filled button per page is the rule. The sheet it opens says it is
 * free, shows the real open times off his calendar (services/quoteSlots, the same slots a quote visit takes), and
 * books the moment they pick. A yard with no address on file is asked for one with the shared address box.
 *
 *   kind   'portal' | 'bid' | 'invoice' — which link the page was opened from; the server resolves the customer
 *   token  that link's token
 */
import React, { useEffect, useState } from 'react';
import { X, CheckCircle, Sparkles } from 'lucide-react';
import { publicApi } from '../services/api';
import { ButtonGhost } from './public';
import AddressAutocomplete from './AddressAutocomplete';

export type WalkStatus = {
  customer_first: string | null; who: string; address: string | null;
  open: { id: number; date: string; time: string; when: string | null; who: string; address: string | null } | null;
  can: boolean; reason: string | null; message: string | null; needs_address: boolean;
  slots: { minutes: number; days: { date: string; label: string; times: string[]; labels: string[] }[] } | null;
};

const DAY_BTN = (on: boolean) => `press flex-shrink-0 px-4 min-h-[58px] rounded-2xl border-2 text-center ${on ? 'bg-brand-600 text-black border-brand-600 shadow-brand' : 'bg-white text-gray-900 border-brand-200 shadow-soft'}`;
const TIME_BTN = (on: boolean) => `press min-h-[50px] rounded-2xl border-2 text-[15px] font-bold ${on ? 'bg-brand-600 text-black border-brand-600 shadow-brand' : 'bg-white text-gray-900 border-brand-200 shadow-soft'}`;

/** The day and time chips: the same picker the referral page uses, for every place a walkthrough is booked. */
export function SlotPicker({ slots, day, time, onDay, onTime, who }: {
  slots: WalkStatus['slots']; day: string; time: string; onDay: (d: string) => void; onTime: (t: string) => void; who?: string;
}) {
  const days = slots?.days || [];
  const row = days.find(d => d.date === day) || null;
  if (!days.length) return <p className="text-[15px] text-gray-800">Nothing is open in the next two weeks. Text us and we will find a time.</p>;
  return (
    <div>
      <p className="text-[14px] text-gray-700 mb-2">A {slots?.minutes || 20}-minute walk of the yard with {who || 'Michael'}. These are the times open.</p>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {days.map(d => (
          <button key={d.date} type="button" aria-pressed={day === d.date} onClick={() => { onDay(d.date); onTime(''); }} className={DAY_BTN(day === d.date)}>
            <span className="block text-[15px] font-black">{d.label.split(',')[0].slice(0, 3)}</span>
            <span className="block text-[13px] opacity-90">{d.label.split(', ')[1]}</span>
          </button>
        ))}
      </div>
      {row && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {row.times.map((t, i) => (
            <button key={t} type="button" aria-pressed={time === t} onClick={() => onTime(t)} className={TIME_BTN(time === t)}>{row.labels[i]}</button>
          ))}
        </div>
      )}
      {day && time && row && (
        <p className="mt-3 text-[15px] font-semibold text-emerald-700 flex items-center gap-1.5">
          <CheckCircle className="h-4 w-4" aria-hidden /> {row.label} at {row.labels[row.times.indexOf(time)]}
        </p>
      )}
    </div>
  );
}

export default function LawnWalkthroughButton({ kind, token, autoOpen = false, className = '' }: {
  kind: 'portal' | 'bid' | 'invoice'; token: string; autoOpen?: boolean; className?: string;
}) {
  const [open, setOpen] = useState(autoOpen);
  if (!token) return null;
  return (
    <>
      <ButtonGhost onClick={() => setOpen(true)} className={`!text-[15px] ${className}`}>
        <Sparkles className="h-5 w-5" aria-hidden /> I want my lawn to look better
      </ButtonGhost>
      {open && <WalkSheet kind={kind} token={token} onClose={() => setOpen(false)} />}
    </>
  );
}

function WalkSheet({ kind, token, onClose }: { kind: string; token: string; onClose: () => void }) {
  const [status, setStatus] = useState<WalkStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [day, setDay] = useState('');
  const [time, setTime] = useState('');
  const [addr, setAddr] = useState({ address: '', city: '', zip: '' });
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState<any>(null);

  const [looks, setLooks] = useState(0);   // bump to read the calendar again (a slot that just went)
  const load = () => setLooks(n => n + 1);
  useEffect(() => {
    let gone = false;
    setLoadError(null);
    publicApi.get(`/walkthrough/public/${kind}/${token}`)
      .then(r => { if (!gone) setStatus(r.data); })
      .catch(e => { if (!gone) setLoadError(e?.response?.data?.message || 'Could not load the times — text us and we will book it by hand.'); });
    return () => { gone = true; };
  }, [kind, token, looks]);

  async function book() {
    if (!day || !time) { setProblem('Pick a day and a time.'); return; }
    if (status?.needs_address && !addr.address.trim()) { setProblem('Type the address of the yard.'); return; }
    setBusy(true); setProblem(null);
    try {
      const body: any = { date: day, time };
      if (status?.needs_address) Object.assign(body, addr);
      setBooked((await publicApi.post(`/walkthrough/public/${kind}/${token}/book`, body)).data);
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.taken) { setProblem('That time just went — pick another.'); setTime(''); load(); }
      else setProblem(d?.message || 'Could not book that — text us and we will sort it out.');
    } finally { setBusy(false); }
  }

  const who = status?.who || 'Michael';
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-black uppercase tracking-tight text-gray-900 text-lg leading-tight">A free lawn walkthrough</h3>
            <p className="text-[14px] text-gray-700 mt-1">{who} walks the yard with you — what's thriving, what's struggling, and what would make the biggest difference. No charge, nothing to sign.</p>
          </div>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        {booked ? (
          <>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-300 p-4">
              <p className="text-[15px] text-gray-900 flex items-start gap-2"><CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden />
                <span><b>{booked.when}</b>{booked.address ? ` at ${booked.address}` : ''}. {booked.who || who} texts you before heading over.</span></p>
              {booked.texted === false && <p className="text-[13px] text-gray-600 mt-2">We could not text a confirmation — it is on the calendar all the same.</p>}
            </div>
            <button onClick={onClose} className="press w-full py-3.5 rounded-xl bg-brand-600 text-black font-semibold">Done</button>
          </>
        ) : loadError ? (
          <p className="text-[15px] text-gray-900">{loadError}</p>
        ) : !status ? (
          <p className="text-[14px] text-gray-500">Looking at the calendar…</p>
        ) : status.open ? (
          <>
            <div className="rounded-2xl bg-brand-50 border border-brand-200 p-4">
              <p className="text-[15px] text-gray-900">You're booked: <b>{status.open.when}</b>{status.open.address ? ` at ${status.open.address}` : ''}. {status.open.who} will text you before heading over.</p>
              <p className="text-[13px] text-gray-600 mt-1">Need another time? Text us and we will move it.</p>
            </div>
            <button onClick={onClose} className="press w-full py-3.5 rounded-xl bg-brand-600 text-black font-semibold">OK</button>
          </>
        ) : !status.can && !status.needs_address ? (
          <p className="text-[15px] text-gray-900">{status.message || 'Text us and we will set a time.'}</p>
        ) : (
          <>
            {status.needs_address ? (
              <div>
                <p className="text-[14px] font-bold text-gray-900 mb-1">Where is the yard?</p>
                <AddressAutocomplete api={publicApi} suggestPath="/portal/address-suggest" placePath="/portal/address-place"
                  value={addr.address} onChange={v => setAddr(a => ({ ...a, address: v }))}
                  onSelect={s => setAddr({ address: s.address || s.label, city: s.city || '', zip: s.zip || '' })}
                  placeholder="Start typing your street" />
              </div>
            ) : status.address ? (
              <p className="text-[14px] text-gray-700">At <b>{status.address}</b>.</p>
            ) : null}
            <SlotPicker slots={status.slots} day={day} time={time} onDay={setDay} onTime={setTime} who={who} />
            {problem && <p className="text-[14px] font-semibold text-red-700">{problem}</p>}
            <button onClick={book} disabled={busy || !day || !time} className="press w-full py-3.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-40">
              {busy ? 'Booking…' : 'Book the walkthrough'}
            </button>
            <p className="text-[12px] text-gray-500 text-center">Free. It goes straight on {who}'s calendar, and you get a text to confirm.</p>
          </>
        )}
      </div>
    </div>
  );
}
